import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Modal,
  Button,
  Loading,
  MultiColumnList,
} from '@folio/stripes/components';

const BulkEditProgressModal = ({ open, progress, isProcessing, results, onClose }) => {
  const showResults = !isProcessing && results;

  const columnMapping = {
    instanceId: 'Instance ID',
    title: 'Title',
    error: 'Error',
  };

  const formatter = {
    instanceId: (item) => item.instanceId,
    title: (item) => item.title || 'Unknown',
    error: (item) => item.error,
  };

  return (
    <Modal
      open={open}
      label={
        showResults ? (
          <FormattedMessage
            id="ui-plugin-inventory-templates.bulkEdit.resultsModal.title"
            defaultMessage="Bulk Edit Results"
          />
        ) : (
          <FormattedMessage
            id="ui-plugin-inventory-templates.bulkEdit.progressModal.title"
            defaultMessage="Processing Bulk Edit..."
          />
        )
      }
      size="large"
      dismissible={false}
      footer={
        showResults ? (
          <Button buttonStyle="primary" onClick={onClose}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.bulkEdit.close"
              defaultMessage="Close"
            />
          </Button>
        ) : null
      }
    >
      <div style={{ padding: '24px 0' }}>
        {isProcessing && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.bulkEdit.progressModal.progress"
                defaultMessage="Progress: {processed}/{total} ({percentage}%)"
                values={{
                  processed: progress.processed,
                  total: progress.total,
                  percentage: progress.percentage,
                }}
              />
            </div>

            <div style={{ marginBottom: '16px', marginTop: '16px' }}>
              <div
                style={{
                  width: '100%',
                  height: '20px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${progress.percentage}%`,
                    height: '100%',
                    backgroundColor: '#4CAF50',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ color: 'green' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.bulkEdit.progressModal.successful"
                  defaultMessage="Successful: {count}"
                  values={{ count: progress.successful }}
                />
              </div>
              <div style={{ color: 'red' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.bulkEdit.progressModal.failed"
                  defaultMessage="Failed: {count}"
                  values={{ count: progress.failed }}
                />
              </div>
            </div>

            {progress.currentItem && (
              <div
                style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}
              >
                {progress.currentItem}
              </div>
            )}
          </>
        )}

        {!isProcessing && !results && (
          <div style={{ textAlign: 'center' }}>
            <Loading size="large" />
          </div>
        )}

        {showResults && (
          <>
            <div
              style={{
                marginBottom: '32px',
                padding: '24px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '48px',
                  fontSize: '16px',
                }}
              >
                <div>
                  <div style={{ color: '#666', marginBottom: '8px' }}>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.bulkEdit.resultsModal.total"
                      defaultMessage="Total:"
                    />
                  </div>
                  <div style={{ fontSize: '28px', color: '#333' }}>
                    {results.total}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '8px' }}>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.bulkEdit.resultsModal.successful"
                      defaultMessage="Successful:"
                    />
                  </div>
                  <div style={{ fontSize: '28px', color: '#28a745' }}>
                    {results.successful}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '8px' }}>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.bulkEdit.resultsModal.failed"
                      defaultMessage="Failed:"
                    />
                  </div>
                  <div style={{ fontSize: '28px', color: '#dc3545' }}>
                    {results.failed}
                  </div>
                </div>
              </div>
            </div>

            {results.failures && results.failures.length > 0 && (
              <>
                <h3>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.bulkEdit.resultsModal.failedInstances"
                    defaultMessage="Failed Instances"
                  />
                </h3>
                <MultiColumnList
                  contentData={results.failures}
                  visibleColumns={['instanceId', 'title', 'error']}
                  columnMapping={columnMapping}
                  formatter={formatter}
                  autosize
                  virtualize
                />
              </>
            )}

            {(!results.failures || results.failures.length === 0) && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '32px',
                  backgroundColor: '#d4edda',
                  borderRadius: '8px',
                  color: '#155724',
                  fontSize: '18px',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.bulkEdit.resultsModal.allSuccessful"
                  defaultMessage="All instances were updated successfully!"
                />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

BulkEditProgressModal.propTypes = {
  open: PropTypes.bool.isRequired,
  progress: PropTypes.shape({
    total: PropTypes.number,
    processed: PropTypes.number,
    successful: PropTypes.number,
    failed: PropTypes.number,
    currentItem: PropTypes.string,
    percentage: PropTypes.number,
  }).isRequired,
  isProcessing: PropTypes.bool.isRequired,
  results: PropTypes.shape({
    total: PropTypes.number,
    successful: PropTypes.number,
    failed: PropTypes.number,
    failures: PropTypes.arrayOf(
      PropTypes.shape({
        instanceId: PropTypes.string,
        title: PropTypes.string,
        error: PropTypes.string,
      }),
    ),
  }),
  onClose: PropTypes.func.isRequired,
};

export default BulkEditProgressModal;
